/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { Fragment, useMemo } from "react";
import ServicesCard from "../booking/ServicesCard";
import ProviderCard from "../booking/ProviderCard";
import Button from "../ui/Button";
import DateTimeCard from "../booking/DateTimeCard";
import ButtonGroup from "../ui/ButtonGroup";
import CancelationModal from "../booking/CancelationModal";
import { useNavigate } from "react-router-dom";
import { BOOKING_STATUS, INVOICE_STATUS, ROUTES } from "../../constants/constants";
import { useBooking } from "../../hooks/useBooking";
import useBookingStatus from "../../hooks/useBookingStatus";
import { useAnyUnit } from "../../lib/react-query/useAnyUnit";
import usePluginUtils from "../../hooks/usePluginUtils";
import Icon from "../ui/Icon";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/DropdownMenu";
import AddToCalendarModal from "../booking/AddToCalendarModal";
import { getBookingProducts } from "../../utils/getBookingProducts";
import StatusRibbon from "../ui/StatusRibbon";
import { SectionedCard, SectionedCardHeader, SectionedCardBody, SectionedCardFooter } from "../ui/SectionedCard";
import LocationSummary from "../LocationSummary";

const bookingStatusTone = {
  [BOOKING_STATUS.CANCELLED]: "error",
  [BOOKING_STATUS.PENDING]: "warning",
  [BOOKING_STATUS.CONFIRMED]: "success",
  [BOOKING_STATUS.ERROR]: "error",
  [BOOKING_STATUS.DELAY]: "warning",
  [BOOKING_STATUS.PENDING_DELAY]: "warning",
};

const ClientBookingsItem = ({ booking, isUpcoming, showCancellationModal = false }) => {
  const navigate = useNavigate();
  const { setSelectedServices, setSelectedProvider, setSelectedLocation } = useBooking();
  const { isPluginEnabled } = usePluginUtils();
  const { data: anyUnit } = useAnyUnit({
    enabled: isPluginEnabled("any_unit"),
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  const hideProvider = !!anyUnit?.hide_other_providers;
  const bookingEntities = useMemo(() => ({ service: booking.service, provider: booking.provider, location: booking.location }), [booking]);
  const { canBeCancelled, shouldShowOnlineMeetingLink, shouldShowAddToCalendar } = useBookingStatus({
    bookingData: booking,
    invoiceData: booking.invoice || null,
    initialStatus: booking.status,
  });

  const products = getBookingProducts(booking);

  const handleBookAgain = () => {
    setSelectedServices([booking.service]);
    setSelectedProvider(booking.provider);
    setSelectedLocation(booking.location);
    navigate(ROUTES.HOME);
  };

  const handleViewInvoice = () => {
    const isInvoicePaid = booking.invoice?.status === INVOICE_STATUS.PAID && booking.invoice?.payment_received;
    const route = isInvoicePaid ? ROUTES.INVOICE_DETAILS : ROUTES.INVOICE_PAY;

    navigate(route.replace(":id", booking.invoice_id));
  };

  const handleOpenOnlineMeeting = () => {
    window.open(booking.online_meeting_link, "_blank");
  };

  const handleReschedule = () => {
    navigate(
      ROUTES.RESCHEDULE_BOOKING.replace(":id", booking.id),
      { state: { from: ROUTES.CLIENT_BOOKINGS } },
    );
  };

  const dropdownActions = [
    {
      key: "addToCalendar",
      visible: shouldShowAddToCalendar,
      render: () => (
        <AddToCalendarModal
          bookings={[booking]}
          bookingEntities={bookingEntities}
          onlineMeetingLink={booking.online_meeting_link}
          trigger={
            <DropdownMenuItem icon={<Icon.CalendarPlus />} title="add_to_calendar" />
          }
        />
      ),
    },
    {
      key: "onlineMeeting",
      visible: shouldShowOnlineMeetingLink && !!booking.online_meeting_link,
      render: () => (
        <DropdownMenuItem
          icon={<Icon.VideoIcon />}
          title="Open online meeting"
          onClick={handleOpenOnlineMeeting}
        />
      ),
    },
    {
      key: "reschedule",
      visible: !!booking.can_reschedule_booking,
      render: () => (
        <DropdownMenuItem
          icon={<Icon.DoubleArrowRight />}
          title="Reschedule booking"
          onClick={handleReschedule}
        />
      ),
    },
    {
      key: "viewInvoice",
      visible: !!booking.invoice_id,
      render: () => (
        <DropdownMenuItem
          icon={<Icon.FileList />}
          title="View invoice"
          onClick={handleViewInvoice}
        />
      ),
    },
    {
      key: "cancel",
      visible: isUpcoming && canBeCancelled,
      render: () => (
        <CancelationModal
          booking={booking}
          trigger={
            <DropdownMenuItem
              variant="danger"
              icon={<Icon.BlockIcon color="danger" />}
              title="cancel booking"
            />
          }
        />
      ),
    },
  ];

  const visibleActions = dropdownActions.filter((action) => action.visible);
  const hasDropdownItems = visibleActions.length > 0;

  return (
    <SectionedCard>
      <StatusRibbon tone={bookingStatusTone[booking.status]} title={`status_${booking.status}`} />
      <SectionedCardHeader>
        <DateTimeCard
          date={booking.start_datetime}
          timeFrom={booking.start_datetime}
          timeTo={booking.end_datetime}
          showIcon={false}
        />
      </SectionedCardHeader>
      <SectionedCardBody $gap="1rem">
        <LocationSummary location={booking.location} />
        {!hideProvider && <ProviderCard provider={booking.provider} />}
        <ServicesCard services={[{ ...booking.service, products }]} />
      </SectionedCardBody>
      <SectionedCardFooter>
        <ButtonGroup gap="large">
          <Button variant="primary" title="Book again" onClick={handleBookAgain} fullWidth />
          {hasDropdownItems && (
            <DropdownMenu placement="bottom-right">
              <DropdownMenuTrigger>
                <Button variant="outline" size="iconLarge" icon={<Icon.KebabIcon />} />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {visibleActions.map((action) => (
                  <Fragment key={action.key}>{action.render()}</Fragment>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </ButtonGroup>
      </SectionedCardFooter>
      {showCancellationModal && canBeCancelled && (
        <CancelationModal booking={booking} show />
      )}
    </SectionedCard>
  );
};

export default ClientBookingsItem;
